// @erp/module-auth/fe/directory — the resolver that fills @erp/ui/actor-name's
// slot, and the two directory reads behind it.
//
// WHY A BATCHER: a provenance line asks for one id, a ledger column asks for
// twenty, and a screen may mount both. Each `<ActorName/>` owns its own
// solid-query entry (so a name resolved once is cached for every later mount,
// keyed by id), but the FETCH is shared: ids that arrive in the same microtask
// leave as ONE `GET /api/auth/directory?ids=a,b,c`. The batch is capped at the
// route's 100-id chunk; a larger burst simply becomes two requests.
import { queryOptions, useQuery } from "@tanstack/solid-query";
import type { Accessor } from "solid-js";
import { unwrap } from "@erp/api-client";
import type { ActorIdentity, ActorResolver, ActorSearch } from "@erp/ui/actor-name";
import { authApi } from "./client.js";

/** One directory answer — the wire shape of GET /api/auth/directory. */
interface DirectoryPage {
  data: ActorIdentity[];
}

const BATCH_MAX = 100;

let pending: Map<string, { resolve: (v: ActorIdentity | null) => void; reject: (e: unknown) => void }[]> | null = null;

/** Flush the ids gathered this tick as one request; unknown ids resolve to null. */
async function flush(): Promise<void> {
  const batch = pending;
  pending = null;
  if (!batch) return;
  const ids = [...batch.keys()];
  for (let i = 0; i < ids.length; i += BATCH_MAX) {
    const slice = ids.slice(i, i + BATCH_MAX);
    try {
      const page = await unwrap<DirectoryPage>(authApi.directory.$get({ query: { ids: slice.join(",") } }));
      const found = new Map(page.data.map((p) => [p.id, p]));
      for (const id of slice) for (const w of batch.get(id) ?? []) w.resolve(found.get(id) ?? null);
    } catch (e) {
      for (const id of slice) for (const w of batch.get(id) ?? []) w.reject(e);
    }
  }
}

/** Ask the directory for one id, joining whatever else asks in the same tick. */
function lookupBatched(id: string): Promise<ActorIdentity | null> {
  return new Promise((resolve, reject) => {
    if (!pending) {
      pending = new Map();
      queueMicrotask(() => void flush());
    }
    const waiters = pending.get(id) ?? [];
    waiters.push({ resolve, reject });
    pending.set(id, waiters);
  });
}

export const directoryQueries = {
  /** Who is this id? Null when the directory does not know (a deleted account). */
  actor: (id: string) =>
    queryOptions({
      queryKey: ["auth", "directory", "actor", id] as const,
      queryFn: () => lookupBatched(id),
      // A name changes rarely and a stale one is harmless; an hour keeps a
      // long editing session from re-asking on every panel open.
      staleTime: 60 * 60_000,
    }),

  /** People whose name or address contains the needle — for a lookup control. */
  search: (needle: string, limit = 10) =>
    queryOptions({
      queryKey: ["auth", "directory", "search", needle, limit] as const,
      queryFn: () =>
        unwrap<DirectoryPage>(authApi.directory.$get({ query: { search: needle, limit: String(limit) } })).then(
          (p) => p.data,
        ),
      staleTime: 60_000,
      enabled: needle.trim().length > 0,
    }),
};

/**
 * The slot's search — the shell passes this to `ActorSearchProvider`. A plain
 * promise (no query entry): a lookup control debounces and race-guards its own
 * requests, and a filter's needle is not worth a cache key.
 */
export const searchActors: ActorSearch = async (query, limit = 10) => {
  const needle = query.trim();
  if (!needle) return [];
  const page = await unwrap<DirectoryPage>(authApi.directory.$get({ query: { search: needle, limit: String(limit) } }));
  return page.data;
};

/**
 * The slot's resolver — the shell passes this to `ActorDirectoryProvider`.
 * Runs inside the consuming component's reactive scope (the slot's contract),
 * so the query it owns follows that component's lifetime, and an id that
 * changes re-resolves.
 */
export const resolveActor: ActorResolver = (id: Accessor<string>) => {
  const q = useQuery(() => ({ ...directoryQueries.actor(id()), enabled: id().length > 0 }));
  return () => q.data ?? undefined;
};
